import { api } from "./client";
import type {
  Account,
  AccountPeople,
  DealPeople,
  Handoff,
  HandoffStatus,
  MyWork,
  ActionItem,
  ActionItemCreate,
  ActionItemUpdate,
  Activity,
  ActivityDetail,
  AuthUrl,
  Blocker,
  Contact,
  ContactCreate,
  ContactKind,
  ContactListItem,
  ContactUpdate,
  DashboardKPIs,
  Deal,
  DealAccessGrant,
  DealContact,
  DealCreate,
  DealUpdate,
  IntegrationProviderStatus,
  Invitation,
  Organization,
  Owner,
  Stage,
  SyncSummary,
  TimeseriesPoint,
  User,
  UserDealGrant,
} from "./types";

// ─── Auth ───────────────────────────────────────────────────
export const Auth = {
  login: (email: string, password: string) =>
    api
      .post<{
        access_token: string;
        token_type: "bearer";
        expires_in: number;
      }>("/auth/login", { email, password })
      .then((r) => r.data),

  me: () => api.get<User>("/auth/me").then((r) => r.data),

  changePassword: (current_password: string, new_password: string) =>
    api.post("/auth/change-password", { current_password, new_password }),

  forgotPassword: (email: string) =>
    api.post<{ message: string }>("/auth/forgot-password", { email }).then((r) => r.data),

  resetPassword: (token: string, new_password: string) =>
    api
      .post<{ message: string }>("/auth/reset-password", { token, new_password })
      .then((r) => r.data),
};

// ─── Users (admin) ──────────────────────────────────────────
export const Users = {
  list: () => api.get<User[]>("/users").then((r) => r.data),
  create: (payload: { email: string; name: string; password: string; role: "admin" | "user" }) =>
    api.post<User>("/users", payload).then((r) => r.data),
  deactivate: (id: string) => api.delete(`/users/${id}`),
  activate: (id: string) => api.post<User>(`/users/${id}/activate`).then((r) => r.data),
  dealAccess: (id: string) =>
    api.get<UserDealGrant[]>(`/users/${id}/deal-access`).then((r) => r.data),
};

// ─── Invitations ────────────────────────────────────────────
export const Invitations = {
  list: () => api.get<Invitation[]>("/invitations").then((r) => r.data),
  create: (payload: { email: string; role: "admin" | "user"; org_id?: string }) =>
    api.post<Invitation>("/invitations", payload).then((r) => r.data),
  revoke: (id: string) => api.delete(`/invitations/${id}`),
  accept: (token: string, payload: { first_name: string; last_name: string; password: string }) =>
    api.post<User>(`/invitations/${token}/accept`, payload).then((r) => r.data),
};

// ─── Organizations (platform-admin) ─────────────────────────
export const Organizations = {
  list: () => api.get<Organization[]>("/organizations").then((r) => r.data),
  create: (payload: { name: string; slug: string; kind?: "internal" | "client" }) =>
    api.post<Organization>("/organizations", payload).then((r) => r.data),
};

// ─── Deal access (admin) ────────────────────────────────────
export const DealAccessApi = {
  list: (dealId: string) =>
    api.get<DealAccessGrant[]>(`/deals/${dealId}/access`).then((r) => r.data),
  grant: (
    dealId: string,
    payload: { user_id: string; capacity: "owner" | "viewer"; is_primary?: boolean },
  ) => api.post<DealAccessGrant>(`/deals/${dealId}/access`, payload).then((r) => r.data),
  revoke: (dealId: string, userId: string) => api.delete(`/deals/${dealId}/access/${userId}`),
};

// ─── Deals ──────────────────────────────────────────────────
export const Deals = {
  list: (params?: { q?: string; stage?: string; owner_user_id?: string }) =>
    api.get<Deal[]>("/deals", { params }).then((r) => r.data),

  get: (id: string) => api.get<Deal>(`/deals/${id}`).then((r) => r.data),

  create: (payload: DealCreate) => api.post<Deal>("/deals", payload).then((r) => r.data),

  update: (id: string, payload: DealUpdate) =>
    api.patch<Deal>(`/deals/${id}`, payload).then((r) => r.data),

  remove: (id: string) => api.delete(`/deals/${id}`),
};

// ─── Accounts ──────────────────────────────────────────────
export const Accounts = {
  list: (params?: { q?: string }) =>
    api.get<Account[]>("/accounts", { params }).then((r) => r.data),
  get: (id: string) => api.get<Account>(`/accounts/${id}`).then((r) => r.data),
  create: (payload: { name: string; notes?: string | null }) =>
    api.post<Account>("/accounts", payload).then((r) => r.data),
  update: (id: string, payload: { name?: string; notes?: string | null }) =>
    api.patch<Account>(`/accounts/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/accounts/${id}`),
};

// ─── Activities ────────────────────────────────────────────
export const Activities = {
  list: (dealId: string) => api.get<Activity[]>(`/deals/${dealId}/activities`).then((r) => r.data),

  create: (
    dealId: string,
    payload: {
      type: Activity["type"];
      date: string;
      text?: string;
      author?: string;
      detail?: Partial<ActivityDetail>;
      occurred_at?: string;
    },
  ) => api.post<Activity>(`/deals/${dealId}/activities`, payload).then((r) => r.data),

  remove: (dealId: string, activityId: string) =>
    api.delete(`/deals/${dealId}/activities/${activityId}`),

  feed: (params?: {
    limit?: number;
    owner_user_id?: string;
    account_id?: string;
    deal_id?: string;
    date_from?: string;
    date_to?: string;
  }) =>
    api.get<Activity[]>(`/activities`, { params: { limit: 200, ...params } }).then((r) => r.data),

  syncGoogle: (dealId: string) =>
    api.post<SyncSummary>(`/deals/${dealId}/sync/google`).then((r) => r.data),
};

// ─── Contacts ──────────────────────────────────────────────
export const Contacts = {
  list: (params?: { q?: string; kind?: ContactKind }) =>
    api.get<ContactListItem[]>("/contacts", { params }).then((r) => r.data),
  create: (payload: ContactCreate) => api.post<Contact>("/contacts", payload).then((r) => r.data),
  update: (id: string, payload: ContactUpdate) =>
    api.patch<Contact>(`/contacts/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete(`/contacts/${id}`),
};

// ─── Deal ↔ Contact links ──────────────────────────────────
export const DealContacts = {
  list: (dealId: string) => api.get<DealContact[]>(`/deals/${dealId}/contacts`).then((r) => r.data),
  attach: (
    dealId: string,
    payload: { contact_id?: string; contact?: ContactCreate; relationship?: string | null },
  ) => api.post<DealContact>(`/deals/${dealId}/contacts`, payload).then((r) => r.data),
  detach: (dealId: string, contactId: string) =>
    api.delete(`/deals/${dealId}/contacts/${contactId}`),
};

// ─── Action items ──────────────────────────────────────────
export const ActionItems = {
  list: (dealId: string) =>
    api.get<ActionItem[]>(`/deals/${dealId}/action-items`).then((r) => r.data),
  create: (dealId: string, payload: ActionItemCreate) =>
    api.post<ActionItem>(`/deals/${dealId}/action-items`, payload).then((r) => r.data),
  update: (dealId: string, itemId: string, payload: ActionItemUpdate) =>
    api.patch<ActionItem>(`/deals/${dealId}/action-items/${itemId}`, payload).then((r) => r.data),
  remove: (dealId: string, itemId: string) => api.delete(`/deals/${dealId}/action-items/${itemId}`),
  feed: () => api.get<ActionItem[]>(`/action-items`).then((r) => r.data),
};

// ─── Config tables ─────────────────────────────────────────
export const Config = {
  // Owners are now org users (read-only here). The create/update/delete
  // owner endpoints were retired (return 410) when owners became users.
  listOwners: () => api.get<Owner[]>("/config/owners").then((r) => r.data),

  listStages: () => api.get<Stage[]>("/config/stages").then((r) => r.data),
  createStage: (payload: Partial<Stage>) =>
    api.post<Stage>("/config/stages", payload).then((r) => r.data),
  updateStage: (id: string, payload: Partial<Stage>) =>
    api.patch<Stage>(`/config/stages/${id}`, payload).then((r) => r.data),
  deleteStage: (id: string) => api.delete(`/config/stages/${id}`),

  listBlockers: () => api.get<Blocker[]>("/config/blockers").then((r) => r.data),
  createBlocker: (payload: { name: string; position?: number }) =>
    api.post<Blocker>("/config/blockers", payload).then((r) => r.data),
  deleteBlocker: (id: string) => api.delete(`/config/blockers/${id}`),
};

// ─── Dashboard ─────────────────────────────────────────────
export const Dashboard = {
  kpis: () => api.get<DashboardKPIs>("/dashboard").then((r) => r.data),
  timeseries: (days = 180) =>
    api.get<TimeseriesPoint[]>("/dashboard/timeseries", { params: { days } }).then((r) => r.data),
  snapshot: () => api.post<TimeseriesPoint>("/dashboard/snapshot").then((r) => r.data),
};

// ─── Integrations ──────────────────────────────────────────
export const Integrations = {
  list: () => api.get<IntegrationProviderStatus[]>("/integrations").then((r) => r.data),
  status: (provider: string) =>
    api.get<IntegrationProviderStatus>(`/integrations/${provider}/status`).then((r) => r.data),
  connect: (provider: string) =>
    api.post<AuthUrl>(`/integrations/${provider}/connect`).then((r) => r.data),
  disconnect: (provider: string) =>
    api.post<IntegrationProviderStatus>(`/integrations/${provider}/disconnect`).then((r) => r.data),
};

// ─── Import / export ───────────────────────────────────────
export const IO = {
  exportUrl: () => `${api.defaults.baseURL}/io/export`,
  /** Download the Excel export with the user's token (a plain link can't send it). */
  downloadExport: async () => {
    const res = await api.get<Blob>("/io/export", { responseType: "blob" });
    const disposition = String(res.headers["content-disposition"] ?? "");
    const name = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? "pipeline.xlsx";
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  },
  importExcel: async (file: File, mode: "upsert" | "replace" = "upsert") => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post(`/io/import?mode=${mode}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data as { imported: number; updated: number; mode: string };
  },
};

// ─── Handoffs ──────────────────────────────────────────────
export const Handoffs = {
  forDeal: (dealId: string) => api.get<Handoff[]>(`/deals/${dealId}/handoffs`).then((r) => r.data),
  create: (dealId: string, payload: { to_user_id: string; note?: string | null }) =>
    api.post<Handoff>(`/deals/${dealId}/handoffs`, payload).then((r) => r.data),
  list: (status?: HandoffStatus) =>
    api.get<Handoff[]>("/handoffs", { params: status ? { status } : {} }).then((r) => r.data),
  accept: (id: string) => api.post<Handoff>(`/handoffs/${id}/accept`).then((r) => r.data),
  decline: (id: string) => api.post<Handoff>(`/handoffs/${id}/decline`).then((r) => r.data),
  cancel: (id: string) => api.post<Handoff>(`/handoffs/${id}/cancel`).then((r) => r.data),
};

// ─── My work (Today) ───────────────────────────────────────
export const Work = {
  mine: () => api.get<MyWork>("/me/work").then((r) => r.data),
};

// ─── People & relationships ────────────────────────────────
export const People = {
  forDeal: (dealId: string) => api.get<DealPeople>(`/deals/${dealId}/people`).then((r) => r.data),
  forAccount: (accountId: string) =>
    api.get<AccountPeople>(`/accounts/${accountId}/people`).then((r) => r.data),
};
